// upload-treatment-photos.component.ts (VERSÃO REATORADA)

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TreatmentPlanService } from 'src/app/services/treatment-plan.service';
import { QrLinkService } from 'src/app/services/qr-link.service';
import { unwrapError } from 'src/app/utils/log-error';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { PhotoCaptureResult, PhotoCaptureService } from '../photo-capture.service';

@Component({
  selector: 'app-upload-treatment-photos',
  templateUrl: './upload-treatment-photos.component.html',
  styleUrls: ['./upload-treatment-photos.component.sass']
})
export class UploadTreatmentPhotosComponent implements OnInit {
  // Parâmetros da URL
  treatmentPlanId: string = '';
  token: string = '';
  photoType: string = '';

  // Getter para compatibilidade com o template
  get photoTypeParam(): string {
    return this.photoType;
  }

  // Controle de UI. loading inicia true: enquanto o backend não resolve
  // o ?qr=... a UI principal fica escondida e o spinner padrão aparece.
  // Sem isso, as áreas (MARKING/PRE/POST/IMMEDIATE_POST) piscam visíveis
  // antes do photoType ser detectado e os *ngIf colapsarem.
  loading: boolean = true;
  uploadComplete: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  selectedPhotoType: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST' = 'MARKING';
  hasUploadedPhotos: boolean = false;

  // PROPRIEDADES PARA O MODAL DE PROGRESSO
  showUploadProgress: boolean = false;
  uploadProgress: number = 0;
  totalFilesToUpload: number = 0;
  filesUploaded: number = 0;
  currentUploadingFileName: string = '';

  // Controle para popup de seleção de câmera/galeria
  showCaptureOptions: boolean = false;
  hasCamera: boolean = false;

  // Flag para indicar processamento HEIC
  processingHeic: boolean = false;

  // Fotos
  photos: {
    id: string | null; // Manter para compatibilidade de estrutura, mas não será usado
    file: File | null;
    preview: SafeUrl | null;
    type: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST';
    label: string;
  }[] = [];

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private treatmentPlanService: TreatmentPlanService,
    private photoCaptureService: PhotoCaptureService,
    private contextService: ContextService,
    private qrLinkService: QrLinkService
  ) { }

  async ngOnInit(): Promise<void> {
    // Tombamento total: URL pública agora carrega APENAS um token curto (?qr=...).
    // Backend resolve o token, retorna params + HMAC token de 30min. Esquema
    // antigo (?token=<JWT>&treatmentPlanId=...) não é mais suportado.
    this.route.queryParams.subscribe(async (params) => {
      const qrToken = String(params['qr'] || '').trim();

      if (!qrToken) {
        this.errorMessage = 'Link inválido ou incompleto. Solicite um novo QR code à clínica.';
        this.loading = false;
        return;
      }

      try {
        const resolved = await firstValueFrom(this.qrLinkService.resolveTreatmentPhotos(qrToken));

        const p = resolved.params || {};
        this.treatmentPlanId = String(p['treatmentPlanId'] || '').trim();
        this.photoType = String(p['photoType'] || '').trim();

        // Modules / moduleId
        const modulesParam = p['modules'];
        const moduleId = p['moduleId'];
        try {
          if (modulesParam) {
            const decoded = JSON.parse(atob(String(modulesParam)));
            if (Array.isArray(decoded)) {
              this.authService.setModules(decoded);
            }
          }
          if (moduleId) {
            const modules = this.authService.getModules();
            const sel = modules.find((m: any) => m.id === moduleId);
            if (sel) {
              this.authService.setSelectedModule(sel);
            }
          }
        } catch (e) {
          console.warn('Falha ao aplicar módulos do deep link', e);
        }

        const clinicId = String(p['clinicId'] || '').trim();
        if (clinicId) {
          this.contextService.setActiveClinic({ id: clinicId, cnpj: clinicId, name: '' });
        }

        // HMAC token de 30 min — autentica requests subsequentes
        this.token = resolved.hmacToken;
        this.authService.setToken(this.token);
        this.authService.setAuthenticated(true);

        if (!this.treatmentPlanId) {
          this.errorMessage = 'QR com parâmetros inválidos. Solicite um novo QR code à clínica.';
          this.loading = false;
          return;
        }

        console.info('[UPLOAD TREATMENT PHOTOS] QR resolvido com sucesso', {
          qrToken,
          treatmentPlanId: this.treatmentPlanId,
          photoType: this.photoType || undefined,
          hasClinicId: !!clinicId,
          hmacExpiresAt: resolved.expiresAt,
        });

        // Resolução completa — esconde spinner e revela UI já filtrada por photoType.
        this.loading = false;
      } catch (err: unknown) {
        const details = unwrapError(err);
        console.error('[UPLOAD TREATMENT PHOTOS] Falha ao resolver QR', {
          qrToken,
          ...details,
        });
        if (details.status === 404) {
          this.errorMessage = 'QR não encontrado. Solicite um novo link à clínica.';
        } else if (details.status === 400 && details.serverMessage) {
          this.errorMessage = details.serverMessage;
        } else if (details.status === 0 || details.name === 'TimeoutError') {
          this.errorMessage = 'Sem conexão. Verifique sua internet e tente abrir o link de novo.';
        } else {
          this.errorMessage = details.serverMessage || 'Não foi possível abrir o link. Tente novamente.';
        }
        this.loading = false;
      }
    });

    // Usa o serviço para detectar as capacidades do dispositivo
    const capabilities = await this.photoCaptureService.detectDeviceCapabilities();
    this.hasCamera = capabilities.hasCamera;
  }

  // Abre o modal de opções
  addNewPhoto(type: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST'): void {
    this.selectedPhotoType = type;
    this.showCaptureOptions = true;
  }

  cancelCaptureOptions(): void {
    this.showCaptureOptions = false;
  }

  // MÉTODO NOVO: Chama o serviço para usar a câmera
  async captureWithCamera(): Promise<void> {
    this.showCaptureOptions = false;
    try {
      this.processingHeic = true;
      const result = await this.photoCaptureService.openNativeCamera();
      this.processPhotoResult(result, this.selectedPhotoType);
    } catch (error) {
      console.warn('Captura de foto cancelada:', error);
    } finally {
      this.processingHeic = false;
    }
  }

  // MÉTODO NOVO: Chama o serviço para usar a galeria
  async selectFromGallery(): Promise<void> {
    this.showCaptureOptions = false;
    try {
      this.processingHeic = true;
      const results = await this.photoCaptureService.openMultipleFileSelector();
      results.files.forEach(result => {
        this.processPhotoResult(result, this.selectedPhotoType);
      });
      if (results.totalCount > 0) {
        this.successMessage = `${results.totalCount} foto(s) selecionada(s) com sucesso!`;
        setTimeout(() => this.successMessage = '', 3000);
      }
    } catch (error) {
      console.warn('Seleção da galeria cancelada:', error);
    } finally {
      this.processingHeic = false;
    }
  }

  // MÉTODO NOVO: Centraliza o processamento do resultado do serviço
  private processPhotoResult(result: PhotoCaptureResult, type: 'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST'): void {
    const typeLabels: Record<string, string> = {
      'MARKING': 'Marcação',
      'PRE': 'Pré-Tratamento',
      'IMMEDIATE_POST': 'Pós-Imediato',
      'POST': 'Pós-Tratamento'
    };
    const existingCount = this.photos.filter(p => p.type === type).length;
    this.photos.push({
      id: null,
      file: result.file,
      preview: result.preview,
      type: type,
      label: `${typeLabels[type] || type} ${existingCount + 1}`
    });
  }

  // Lógica de remoção simplificada
  removePhoto(photoToRemove: any): void {
    this.photos = this.photos.filter(p => p !== photoToRemove);
  }

  async uploadPhotos(): Promise<void> {
    const photosToUpload = this.photos.filter(p => p.file);
    if (photosToUpload.length === 0) {
      this.errorMessage = 'Nenhuma nova foto selecionada para enviar.';
      return;
    }

    // 1. INICIALIZA O ESTADO DO PROGRESSO
    this.totalFilesToUpload = photosToUpload.length;
    this.filesUploaded = 0;
    this.uploadProgress = 0;
    this.showUploadProgress = true; // Mostra o modal de progresso
    this.errorMessage = '';

    // 2. FAZ O UPLOAD DE CADA FOTO, UMA POR UMA (SEQUENCIALMENTE)
    for (const photo of photosToUpload) {
      try {
        this.currentUploadingFileName = photo.file!.name;

        const fileRequest = { file_name: photo.file!.name, content_type: photo.file!.type };
        const presignedData = await this.treatmentPlanService.generatePresignedUrls(
          this.treatmentPlanId, [fileRequest]
        );
        const uploadData = presignedData[0];

        await this.treatmentPlanService.uploadFileToS3(uploadData.uploadUrl, photo.file!, photo.file!.type);

        const backendTypeMap: Record<'PRE' | 'POST' | 'MARKING' | 'IMMEDIATE_POST', 'PRE_TREATMENT' | 'POST_TREATMENT' | 'MARKING' | 'IMMEDIATE_POST_TREATMENT'> = {
          'PRE': 'PRE_TREATMENT',
          'POST': 'POST_TREATMENT',
          'MARKING': 'MARKING',
          'IMMEDIATE_POST': 'IMMEDIATE_POST_TREATMENT'
        };

        const backendType = backendTypeMap[photo.type];
        if (!backendType) {
          throw new Error(`Tipo de foto inválido ou não mapeado: ${photo.type}`);
        }

        const photoForDb = {
          photo: uploadData.objectKey,
          photoOrder: 0, // A ordem pode ser ajustada pelo backend
          type: backendType
        };

        await this.treatmentPlanService.uploadTreatmentPhotos(this.treatmentPlanId, [photoForDb], photoForDb.type);

        // 3. ATUALIZA O PROGRESSO APÓS SUCESSO
        this.filesUploaded++;
        this.uploadProgress = (this.filesUploaded / this.totalFilesToUpload) * 100;

        // Limpa o arquivo para não ser reenviado
        photo.file = null;

      } catch (error) {
        console.error(`Falha no upload da foto ${photo.label}:`, error);
        this.errorMessage = `Ocorreu um erro ao enviar "${photo.file?.name}". O processo foi interrompido.`;
        this.showUploadProgress = false; // Esconde o modal em caso de erro
        return; // Interrompe o loop
      }
    }

    // 4. FINALIZA O PROCESSO
    // this.currentUploadingFileName = 'Concluído!';
    this.successMessage = 'Fotos enviadas com sucesso!';
    this.uploadComplete = true;
    this.hasUploadedPhotos = true;

    // Esconde o modal de progresso após um pequeno delay
    setTimeout(() => {
      this.showUploadProgress = false;
      this.photos = []; // Limpa a lista de fotos após o envio bem-sucedido
    }, 1500);
  }

  finish(): void {
    if (!this.canFinish()) {
      this.errorMessage = 'Não é possível finalizar sem enviar pelo menos uma foto.';
      return;
    }
    alert('Você pode fechar esta página agora. As fotos foram enviadas com sucesso!');
  }

  get isUploading(): boolean {
    return this.showUploadProgress;
  }

  // Funções auxiliares para o template
  hasMarkingPhotos(): boolean { return this.photos.some(p => p.type === 'MARKING'); }
  hasPrePhotos(): boolean { return this.photos.some(p => p.type === 'PRE'); }
  hasImmediatePostPhotos(): boolean { return this.photos.some(p => p.type === 'IMMEDIATE_POST'); }
  hasPosPhotos(): boolean { return this.photos.some(p => p.type === 'POST'); }
  hasAnyPhotosForUpload(): boolean { return this.photos.length > 0; }
  canFinish(): boolean { return this.uploadComplete && this.hasUploadedPhotos; }

  // Funções depreciadas (não mais utilizadas, mas mantidas para referência caso precise)
  hasSelectedPhotos(): boolean { return this.photos.some(p => p.file !== null); }
  allPhotosSelected(): boolean { return this.photos.every(p => p.file !== null); }
}